// src/pages/LoginPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/common/Toast";
import TigerLogo from "../components/common/TigerLogo";
import { IconLock } from "../components/common/Icons";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return toast("Fill in all fields", "error");

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      toast("Welcome back!", "success");
      navigate("/");
    } else {
      toast(result.error || "Login failed", "error");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 stripe-pattern" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-tiger-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-tiger-600/5 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md mx-4 animate-fade-in-up">
        {/* Card */}
        <div className="bg-onyx-900/80 backdrop-blur-sm border border-onyx-800/60 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-onyx-900 to-onyx-950 p-8 text-center border-b border-onyx-800/50">
            <div className="absolute inset-0 stripe-pattern opacity-50" />
            <div className="relative">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <TigerLogo size={72} />
                  <div className="absolute inset-0 bg-tiger-500/15 rounded-full blur-xl" />
                </div>
              </div>
              <h1 className="font-display text-2xl font-bold text-tiger-400 tracking-wider">
                EVANDER CAMPUS
              </h1>
              <p className="font-display text-sm text-onyx-500 tracking-widest mt-1">
                PSAL ATHLETICS
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-display text-onyx-500 tracking-wider uppercase mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 bg-onyx-950 border border-onyx-700/50 rounded-lg text-onyx-100 font-body
                    placeholder-onyx-600 focus:outline-none focus:border-tiger-500/50 focus:ring-1 focus:ring-tiger-500/20 transition-all"
                  placeholder="Enter username"
                  autoComplete="username"
                />
              </div>

              <div>
                <label className="block text-xs font-display text-onyx-500 tracking-wider uppercase mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-onyx-950 border border-onyx-700/50 rounded-lg text-onyx-100 font-body
                    placeholder-onyx-600 focus:outline-none focus:border-tiger-500/50 focus:ring-1 focus:ring-tiger-500/20 transition-all"
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg font-display font-semibold tracking-wider text-sm transition-all
                  bg-gradient-to-r from-tiger-600 to-tiger-500 hover:from-tiger-500 hover:to-tiger-400
                  text-white shadow-lg shadow-tiger-500/20 hover:shadow-tiger-500/30
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2"
              >
                <IconLock size={16} />
                {loading ? "SIGNING IN..." : "SIGN IN"}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-onyx-600 mt-6 font-body">
          Evander Childs Campus Athletics · Bronx, NY
        </p>
      </div>
    </div>
  );
}
